import { X } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { brandContent, COLORS, ASSETS } from "../i18n";
import { Reveal } from "../components/Reveal";

export const BrandSystemContent = ({ onClose }) => {
  const { lang, toggle, t } = useLang();
  const c = brandContent[lang];

  const renderSection = (s) => {
    switch (s.id) {
      case "symbol":
        return (
          <div className="space-y-16">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-6">
                <SectionHead s={s} />
                {s.body.map((p, i) => <Body key={i} delay={0.1 + i * 0.05}>{p}</Body>)}
                <Body delay={0.2}>{s.construction}</Body>
              </div>
              <div className="lg:col-span-5 lg:col-start-8">
                <Reveal delay={0.15}>
                  <div className="relative bg-ivory border border-ink/10 aspect-square flex items-center justify-center p-12 overflow-hidden">
                    <svg className="absolute inset-0 w-full h-full text-gold/40" preserveAspectRatio="none" aria-hidden="true">
                      <line x1="50%" y1="0" x2="50%" y2="100%" stroke="currentColor" strokeWidth="1" strokeDasharray="3 5" />
                      <line x1="0" y1="50%" x2="100%" y2="50%" stroke="currentColor" strokeWidth="1" strokeDasharray="3 5" />
                      <rect x="22%" y="7%" width="56%" height="86%" rx="130" fill="none" stroke="currentColor" strokeWidth="1" />
                      <line x1="22%" y1="0" x2="22%" y2="100%" stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 6" />
                      <line x1="78%" y1="0" x2="78%" y2="100%" stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 6" />
                      <line x1="0" y1="7%" x2="100%" y2="7%" stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 6" />
                      <line x1="0" y1="93%" x2="100%" y2="93%" stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 6" />
                    </svg>
                    <img src={ASSETS.sealDark} alt="TOTUMO monogram seal" className="relative max-h-full w-auto" />
                  </div>
                  <p className="mt-3 font-sans text-[0.65rem] uppercase tracking-[0.22em] text-ink/40 text-center">Geometric construction · T + O</p>
                </Reveal>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-x-10 gap-y-6">
              {s.principles.map((p, i) => (
                <Reveal key={p.k} delay={i * 0.06}>
                  <div className="border-t border-ink/15 pt-4">
                    <p className="font-serif text-xl text-ink">{p.k}</p>
                    <p className="mt-1.5 font-serif text-base leading-relaxed text-ink/65">{p.v}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <div>
              <Reveal><p className="font-sans text-[0.7rem] uppercase tracking-[0.25em] text-tobacco mb-6">{s.usesTitle}</p></Reveal>
              <div className="grid sm:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
                <Reveal delay={0}>
                  <div className="bg-white h-full">
                    <div className="aspect-[4/3] flex items-center justify-center p-12 bg-ivory">
                      <img src={ASSETS.sealDark} alt="" className="max-h-full w-auto opacity-90" />
                    </div>
                    <div className="px-5 py-5">
                      <p className="font-serif text-lg text-ink">{s.uses[0].label}</p>
                      <p className="font-serif text-base leading-relaxed text-ink/60 mt-1">{s.uses[0].note}</p>
                    </div>
                  </div>
                </Reveal>
                <Reveal delay={0.06}>
                  <div className="bg-white h-full">
                    <div className="aspect-[4/3] flex items-center justify-center p-12 bg-[#171713]">
                      <img src={ASSETS.sealGold} alt="" className="max-h-full w-auto" />
                    </div>
                    <div className="px-5 py-5">
                      <p className="font-serif text-lg text-ink">{s.uses[1].label}</p>
                      <p className="font-serif text-base leading-relaxed text-ink/60 mt-1">{s.uses[1].note}</p>
                    </div>
                  </div>
                </Reveal>
                <Reveal delay={0.12}>
                  <div className="bg-white h-full">
                    <div className="aspect-[4/3] flex items-center justify-center p-12 bg-[#6B1111]">
                      <img src={ASSETS.sealLight} alt="" className="max-h-full w-auto" />
                    </div>
                    <div className="px-5 py-5">
                      <p className="font-serif text-lg text-ink">{s.uses[2].label}</p>
                      <p className="font-serif text-base leading-relaxed text-ink/60 mt-1">{s.uses[2].note}</p>
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        );
      case "type":
        return (
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <SectionHead s={s} />
              {s.body.map((p, i) => <Body key={i} delay={0.1 + i * 0.05}>{p}</Body>)}
              <Reveal delay={0.2}>
                <div className="mt-10 bg-ivory border border-ink/10 p-10 text-center">
                  <img src={ASSETS.wordmark} alt="TOTUMO wordmark" className="w-full max-w-xs mx-auto" />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 space-y-10">
              <Reveal delay={0.12}>
                <div className="border-t border-ink/15 pt-6">
                  <p className="font-sans text-[0.7rem] uppercase tracking-[0.2em] text-tobacco">{s.primary.title}</p>
                  <p className="font-serif text-5xl md:text-6xl font-light text-ink mt-4 tracking-tight">Aa Gg Oo</p>
                  <p className="mt-4 font-serif text-base leading-relaxed text-ink/70">{s.primary.desc}</p>
                </div>
              </Reveal>
              <Reveal delay={0.18}>
                <div className="border-t border-ink/15 pt-6">
                  <p className="font-sans text-[0.7rem] uppercase tracking-[0.2em] text-tobacco">{s.secondary.title}</p>
                  <p className="font-sans text-5xl md:text-6xl font-light text-ink mt-4 tracking-tight">Aa Gg Oo</p>
                  <p className="mt-4 font-serif text-base leading-relaxed text-ink/70">{s.secondary.desc}</p>
                </div>
              </Reveal>
            </div>
          </div>
        );
      case "color":
        return (
          <div>
            <div className="max-w-2xl"><SectionHead s={s} />{s.body.map((p, i) => <Body key={i} delay={0.1}>{p}</Body>)}</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-ink/10 border border-ink/10 mt-12">
              {COLORS.map((col, i) => (
                <Reveal key={col.hex} delay={i * 0.06}>
                  <div className="bg-white">
                    <div className="h-40 w-full" style={{ backgroundColor: col.hex }} />
                    <div className="p-5">
                      <p className="font-serif text-lg text-ink">{col.name}</p>
                      <p className="font-sans text-xs tracking-[0.15em] text-ink/50 mt-1">{col.hex}</p>
                      <p className="font-serif text-sm leading-relaxed text-ink/65 mt-3">{lang === "es" ? col.es : col.en}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        );
      case "values":
        return (
          <div>
            <SectionHead s={s} />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10 mt-10">
              {s.values.map((v, i) => (
                <Reveal key={v.k} delay={i * 0.05}>
                  <div className="border-t border-ink/15 pt-5">
                    <p className="font-serif text-2xl text-ink">{v.k}</p>
                    <p className="mt-2 font-serif text-base leading-relaxed text-ink/65">{v.v}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        );
      case "visual":
        return (
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5"><SectionHead s={s} />{s.body.map((p, i) => <Body key={i} delay={0.1}>{p}</Body>)}</div>
            <div className="lg:col-span-6 lg:col-start-7 mt-2">
              <ul className="space-y-5">
                {s.rules.map((r, i) => (
                  <Reveal key={i} delay={0.1 + i * 0.05}>
                    <li className="flex gap-4 border-t border-ink/10 pt-4">
                      <span className="font-serif text-gold text-lg leading-none">·</span>
                      <span className="font-serif text-base leading-relaxed text-ink/70">{r}</span>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        );
      case "phrases":
        return (
          <div>
            <SectionHead s={s} />
            <div className="mt-8 flex flex-wrap gap-x-10 gap-y-6">
              {s.phrases.map((p, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <span className="font-serif text-2xl md:text-3xl font-light text-ink/85 italic">{p}</span>
                </Reveal>
              ))}
            </div>
          </div>
        );
      default:
        return (
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5"><SectionHead s={s} /></div>
            <div className="lg:col-span-6 lg:col-start-7">
              {s.body.map((p, i) => <Body key={i} delay={0.1 + i * 0.05}>{p}</Body>)}
              {s.quote && (
                <Reveal delay={0.2}>
                  <p className="mt-8 font-serif text-2xl md:text-3xl font-light italic text-tobacco leading-snug">{s.quote}</p>
                </Reveal>
              )}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="bg-white">
      {/* Sticky top bar */}
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur-md border-b border-ink/10">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={ASSETS.sealDark} alt="TOTUMO" className="h-9 w-auto" />
            <span className="font-serif text-xl tracking-[0.35em] text-ink hidden sm:block">TOTUMO</span>
          </div>
          <div className="flex items-center gap-5">
            <button data-testid="brand-lang-toggle" onClick={toggle} className="font-sans text-[0.7rem] tracking-[0.2em] text-ink/70 hover:text-ink transition-colors duration-500">
              <span className={lang === "en" ? "text-ink" : "text-ink/40"}>EN</span>
              <span className="mx-1 text-ink/30">/</span>
              <span className={lang === "es" ? "text-ink" : "text-ink/40"}>ES</span>
            </button>
            <button data-testid="brand-back-link" onClick={onClose} className="inline-flex items-center gap-2 font-sans text-[0.7rem] uppercase tracking-[0.18em] border border-ink/60 px-5 py-2.5 text-ink hover:bg-ink hover:text-ivory transition-all duration-500">
              {t("cta.back")}
              <X className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-20 pb-20 md:pb-28 px-6 md:px-12 lg:px-16 max-w-screen-2xl mx-auto">
        <Reveal>
          <span className="inline-flex items-center gap-3 font-sans text-[0.7rem] uppercase tracking-[0.3em] text-tobacco">
            <span className="h-px w-8 bg-gold" />{c.eyebrow}
          </span>
        </Reveal>
        <Reveal delay={0.05}>
          <h1 className="mt-8 font-serif text-5xl md:text-7xl font-light tracking-tight text-ink">{c.title}</h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 font-serif italic text-2xl text-tobacco">{c.subtitle}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 font-serif text-lg md:text-xl leading-relaxed text-ink/75 max-w-2xl">{c.intro}</p>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-14 border border-ink/10 bg-ivory px-8 py-16 md:py-20 flex flex-col items-center text-center">
            <img src={ASSETS.sealDark} alt="TOTUMO seal" className="h-20 md:h-24 w-auto" />
            <img src={ASSETS.wordmark} alt="TOTUMO House of Gold" className="mt-8 w-full max-w-md" />
            <p className="mt-5 font-sans text-[0.6rem] uppercase tracking-[0.3em] text-ink/40">Primary lockup · Seal · Wordmark</p>
          </div>
        </Reveal>
      </section>

      {/* Sections */}
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 pb-24 md:pb-32 space-y-24 md:space-y-32">
        {c.sections.map((s) => (
          <section key={s.id} data-testid={`brand-section-${s.id}`} className="border-t border-ink/10 pt-16">
            {renderSection(s)}
          </section>
        ))}
      </div>

      <footer className="bg-[#171713] text-ivory py-16 px-6 md:px-12 lg:px-16">
        <div className="max-w-screen-2xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <img src={ASSETS.sealLight} alt="TOTUMO" className="h-12 w-auto opacity-90" />
          <p className="font-sans text-xs tracking-[0.2em] text-ivory/50">© {new Date().getFullYear()} TOTUMO · HOUSE OF GOLD</p>
        </div>
        <div data-testid="brand-credit" className="max-w-screen-2xl mx-auto mt-8 pt-6 border-t border-ivory/10 text-center">
          <p className="font-sans text-[0.65rem] tracking-[0.18em] text-ivory/45">
            Brand system developed by{" "}
            <a
              data-testid="credit-bnb-link"
              href="https://www.brandandbehavior.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-ivory/70 hover:text-gold underline underline-offset-4 decoration-ivory/20 hover:decoration-gold transition-colors duration-500"
            >
              Brand and Behavior
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default function BrandSystem() {
  return (
    <div className="min-h-screen bg-white">
      <BrandSystemContent onClose={() => { window.location.href = "/"; }} />
    </div>
  );
}

const SectionHead = ({ s }) => (
  <>
    <Reveal>
      <span className="font-sans text-[0.7rem] uppercase tracking-[0.25em] text-tobacco">{s.label}</span>
    </Reveal>
    <Reveal delay={0.05}>
      <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl font-light text-ink leading-tight">{s.heading}</h2>
    </Reveal>
  </>
);

const Body = ({ children, delay = 0.1 }) => (
  <Reveal delay={delay}>
    <p className="mt-6 font-serif text-lg md:text-xl leading-relaxed text-ink/75">{children}</p>
  </Reveal>
);
